import type { FC } from 'react';
import { useEffect, useMemo } from 'react';
import type { SelectProps } from 'components/common';
import { CommonSelect, PlaceholderNoAccess } from 'components/common';
import { type SelectFlowsParams, useInfiniteFlows } from '../api';
import type { CreativeTextDetails } from '../types';
import { useInView } from 'react-intersection-observer';
import { useFormContext } from 'react-hook-form';
import type { SelectChangeEvent } from '@mui/material';
import { usePermissionGuard } from 'features/login';

interface SelectFlowsAttrs {
  open: boolean;
  multiples: boolean;
  placeholder: string;
  params?: SelectFlowsParams;
  creativeText?: CreativeTextDetails;
  name: string;
}

type SelectFlowsProps = Partial<
  Pick<SelectProps, 'handleChange' | 'inputLabel' | 'onClose' | 'dataTestId'> &
  SelectFlowsAttrs
>;

const SelectFlowsComponent: FC<SelectFlowsProps> =
  ({
     handleChange,
     name,
     inputLabel,
     multiples,
     placeholder,
     onClose,
     open,
     params,
     dataTestId,
   }) => {
    const { ref, inView } = useInView({
      threshold: 0,
    });
    const { setValue } = useFormContext();
    const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
      useInfiniteFlows({ params: { ...params, has_default_creative_text: params?.has_default_creative_text } });

    const options = useMemo(() => {
      return data?.pages.flatMap(page => page.data).map(flow => ({
        label: flow.name,
        value: flow.id,
      })) ?? [];
    }, [data]);


    useEffect(() => {
      if (inView && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage();
      }
    }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

    const handleSelectionChange = (event: SelectChangeEvent<unknown>) => {
      const selectedId = event.target.value as number;
      setValue('default_flow_id', selectedId);
      handleChange?.(event);
    };

    return (
      <CommonSelect
        name={name ?? 'flows'}
        inputLabel={inputLabel ?? 'Flows'}
        options={options}
        handleChange={handleSelectionChange}
        placeholder={placeholder}
        multiples={multiples}
        onClose={onClose}
        open={open}
        loading={isFetchingNextPage}
        loadMoreRef={ref}
        dataTestId={dataTestId}
      />
    );
  };

const SelectFlows = (props: SelectFlowsProps) => {
  const { accessToViewTiktokflow } = usePermissionGuard();
  if (!accessToViewTiktokflow) return <PlaceholderNoAccess title={'No access to view flow'} />;

  return (
    <SelectFlowsComponent
      {...props}
    />
  );
};

export default SelectFlows;
